/**
 * The command palette: search, navigate and create from one field, without the mouse.
 *
 * Opened with Ctrl+K from anywhere, including while typing. It is the product's primary
 * navigation surface — every area must be reachable from here.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { Kbd } from "./primitives";

export type Command = {
  id: string;
  label: string;
  group: string;
  hint?: string;
  run: () => void;
};

export function CommandPalette({
  open,
  commands,
  onSearch,
  onClose,
}: {
  open: boolean;
  commands: Command[];
  /** Async source for dynamic results (pages, projects); merged after the static commands. */
  onSearch?: (query: string) => Promise<Command[]>;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [dynamic, setDynamic] = useState<Command[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const matches = useMemo(
    () => [...filterCommands(commands, query), ...dynamic],
    [commands, query, dynamic],
  );
  const active = matches[activeIndex];

  // A fresh query every time it opens; nobody wants yesterday's search waiting for them.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      setDynamic([]);
      inputRef.current?.focus();
    }
  }, [open]);

  // Dynamic results follow the query with a short debounce; stale answers are dropped.
  useEffect(() => {
    if (!open || !onSearch || !query.trim()) {
      setDynamic([]);
      return;
    }
    let stale = false;
    const timer = window.setTimeout(() => {
      void onSearch(query.trim())
        .then((found) => !stale && setDynamic(found))
        .catch(() => !stale && setDynamic([]));
    }, 150);
    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
  }, [open, query, onSearch]);

  // Keep the selection inside the result list as it shrinks while typing.
  useEffect(() => {
    setActiveIndex((current) => (current >= matches.length ? 0 : current));
  }, [matches.length]);

  if (!open) {
    return null;
  }

  const move = (delta: number) => {
    if (matches.length === 0) {
      return;
    }
    // Wraps, so ↑ from the top jumps to the last entry.
    setActiveIndex((current) => (current + delta + matches.length) % matches.length);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Enter":
        event.preventDefault();
        if (active) {
          active.run();
          onClose();
        }
        break;
      case "Escape":
        event.preventDefault();
        onClose();
        break;
    }
  };

  return (
    <div
      ref={overlayRef}
      onMouseDown={(event) => event.target === overlayRef.current && onClose()}
      className="fixed inset-0 z-50 flex items-start justify-center p-s6 pt-[12vh]"
      style={{ background: "color-mix(in srgb, var(--deep) 45%, transparent)" }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Kommandopalett"
        className="nb-animate-panel w-[min(560px,100%)] overflow-hidden rounded-card bg-surface-1 shadow-dialog"
      >
        <div className="border-b border-line p-s4">
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={active ? `${listboxId}-${active.id}` : undefined}
            aria-label="Sök kommandon och sidor"
            placeholder="Sök, gå till eller skapa …"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            className="nb-field h-auto w-full border-0 bg-transparent px-s3 py-s3 text-title focus:shadow-none"
          />
        </div>

        <ul id={listboxId} role="listbox" aria-label="Träffar" className="max-h-[46vh] overflow-y-auto p-s3">
          {matches.length === 0 && (
            <li className="px-s4 py-s6 text-center text-body text-muted">
              Inga träffar för ”{query}”.
            </li>
          )}

          {matches.map((command, index) => {
            const isActive = index === activeIndex;
            return (
              <li key={command.id}>
                <button
                  id={`${listboxId}-${command.id}`}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  // Pointer and keyboard drive the same selection, so they never disagree.
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => {
                    command.run();
                    onClose();
                  }}
                  className={`flex w-full items-center gap-s4 rounded-control px-s4 py-s3 text-left ${
                    isActive ? "bg-surface-2" : "bg-transparent"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`h-s5 w-[3px] rounded-full ${isActive ? "bg-drop" : "bg-transparent"}`}
                  />
                  <span className="flex-1 text-body text-ink">{command.label}</span>
                  {command.hint && <span className="text-small text-muted">{command.hint}</span>}
                  <span className="text-overline uppercase tracking-[0.09em] text-muted">
                    {command.group}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <footer className="flex items-center gap-s5 border-t border-line bg-surface-2 px-s5 py-s3 text-small text-muted">
          <span className="flex items-center gap-s2">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> flytta
          </span>
          <span className="flex items-center gap-s2">
            <Kbd>Enter</Kbd> kör
          </span>
          <span className="flex items-center gap-s2">
            <Kbd>Esc</Kbd> stäng
          </span>
        </footer>
      </div>
    </div>
  );
}

/**
 * Subsequence matching: "kva" finds "Kv. Ankaret". Forgiving enough to be fast to type, strict
 * enough to keep the order of the letters meaningful.
 */
export function filterCommands(commands: Command[], query: string): Command[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return commands;
  }

  return commands.filter((command) => {
    const haystack = `${command.label} ${command.group}`.toLowerCase();
    let position = 0;
    for (const character of needle) {
      if (character === " ") {
        continue;
      }
      position = haystack.indexOf(character, position);
      if (position === -1) {
        return false;
      }
      position += 1;
    }
    return true;
  });
}
